/**
 * DetailCard + FieldRow - shared components for detail pages.
 *
 * DetailCard: card wrapper with a title, mandatory icon, optional trailing element.
 *   - Soft rounded card; the icon sits on a tint of the brand colour beside a
 *     sentence-case title, the same chip the sections view uses
 *   - Built-in collapsible: shows first `previewCount` children by default,
 *     "Show N more" toggle to expand. Pass `collapsible={false}` to disable.
 *
 * FieldRow: label/value row with optional inline edit mode.
 *
 * Used on Registration Detail and Claim Detail pages.
 */

import { type ReactNode, Children, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import { normaliseDetailCardDisplayValue } from './detail-card-formatting'
import { CardDragHandle } from '@/shared/components/card-drag-handle'

// ── DetailCard ──────────────────────────────────────────

/** Default number of child rows shown before collapsing */
const DEFAULT_PREVIEW_COUNT = 5

interface DetailCardProps {
  title: string;
  /** Rendered immediately after the title, inside the header. For a badge that
   *  qualifies the card - a warranty's tier, say - where the title itself has
   *  to stay a plain string because callers interpolate it into labels. */
  titleAccessory?: ReactNode;
  /** Finish class applied to the header bar, so the whole strip carries the
   *  record's tier rather than a badge doing it alone. Title and icon inherit
   *  the finish's own ink, which is light on the dark metals and dark on the
   *  pale ones. */
  headerFinishClass?: string;
  icon: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
  /** Set false to never collapse (default: true - collapses when children exceed previewCount) */
  collapsible?: boolean;
  /** Number of child rows to show before the "Show more" toggle (default: 5) */
  previewCount?: number;
  /** Force expanded - e.g. when editing */
  forceExpanded?: boolean;
  /** Folded away to its header. Pair with `onToggleFold` to show the control. */
  folded?: boolean;
  /** Supplying this renders the fold control; the caller owns the state so it
   *  can persist across sessions. */
  onToggleFold?: () => void;
  /**
   * WPS-166: cap the content area at this height (px) and collapse the rest
   * behind a "Show more".
   *
   * `previewCount` only helps a card whose children are ROWS - it counts them.
   * A card that renders one bespoke block (the contractor card is a single
   * `<div>`) therefore never collapses however long it gets, and in an aligned
   * grid one such card sets the height of everything beside it. This caps by
   * measured height instead, so any card can be reined in regardless of how it
   * is built.
   */
  maxContentHeight?: number;
  /** Stable hook for tests and tooling on the card's root. */
  testId?: string;
}

export function DetailCard({
  title,
  titleAccessory,
  headerFinishClass,
  icon,
  trailing,
  children,
  collapsible = true,
  previewCount = DEFAULT_PREVIEW_COUNT,
  forceExpanded = false,
  folded,
  onToggleFold,
  maxContentHeight,
  testId,
}: DetailCardProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  // Height-capped content: measure whether there is anything to reveal, so the
  // toggle only appears on a card that actually overflows its cap. One observer
  // per cap; a content change that matters resizes the box and re-measures.
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [overflows, setOverflows] = useState(false)
  useEffect(() => {
    if (!maxContentHeight) return
    const el = contentRef.current
    if (!el) return
    const check = () => setOverflows(el.scrollHeight > maxContentHeight + 4)
    check()
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [maxContentHeight])

  const childArray = Children.toArray(children)
  const isExpanded = forceExpanded || expanded || !collapsible
  const shouldCollapse = collapsible && childArray.length > previewCount && !forceExpanded
  const visibleChildren = shouldCollapse && !expanded
    ? childArray.slice(0, previewCount)
    : childArray
  const hiddenCount = childArray.length - previewCount

  return (
    <div
      data-testid={testId}
      className={cn(
        "rounded-2xl border border-[var(--rule)] bg-[var(--bg)] shadow-[var(--shadow-card)]",
        // An aligned grid stretches cards to their row's height; a folded
        // card is only its header, so it keeps its own height rather than
        // reading as an empty box.
        folded && "!h-auto",
      )}
    >
      {/* Header - fixed height so it stays consistent whether or not a
          trailing action is rendered, and titles never wrap onto a second
          line. The band takes the top panel's surface (--panel, cream by
          default, re-themable per tenant) so the card and page header read as
          one family. A tier finish keeps its own metal strip. */}
      <div
        className={cn(
          "relative flex h-[60px] items-center justify-between gap-2 rounded-t-2xl px-5",
          headerFinishClass
            ? `tier-finish ${headerFinishClass}`
            : "border-b border-b-[var(--rule-light)] bg-[var(--panel)]",
          folded && "rounded-b-2xl",
          folded && !headerFinishClass && "border-b-transparent",
        )}
      >
        <div className="relative flex min-w-0 items-center gap-3">
          <CardDragHandle />
          {/* The icon sits on a soft tint of the brand colour, the same chip
              the sections view uses, so a card reads the same in both. */}
          <span
            className={cn(
              "flex shrink-0 items-center justify-center",
              headerFinishClass
                ? "opacity-80"
                : "h-8 w-8 rounded-lg bg-[color-mix(in_srgb,var(--green)_10%,transparent)] text-[var(--green)] [&_svg]:h-4 [&_svg]:w-4",
            )}
          >
            {icon}
          </span>
          <span
            className={cn(
              "truncate",
              headerFinishClass
                ? "text-xs font-normal uppercase tracking-[0.14em] opacity-90"
                : "text-[16px] font-medium text-[var(--ink)]",
            )}
          >
            {title}
          </span>
          {titleAccessory}
        </div>
        <div className="relative flex shrink-0 items-center gap-1">
          {trailing}
          {/* A separate control rather than a clickable header: the header
              already carries actions, and a card that folds when you meant to
              press one of them is worse than no fold at all. */}
          {onToggleFold && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={onToggleFold}
              aria-expanded={!folded}
              aria-label={
                folded
                  ? t("detailCards.expandSection", "Expand section")
                  : t("detailCards.collapseSection", "Collapse section")
              }
              className="text-[var(--ink-3)]"
            >
              {folded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronUp className="h-3.5 w-3.5" />
              )}
            </Button>
          )}
        </div>
      </div>
      {/* Content - white background for contrast */}
      <div
        className={cn("rounded-b-2xl bg-[var(--bg)]", folded && "hidden")}
      >
        <div className="relative">
          <div
            ref={contentRef}
            className={cn(
              maxContentHeight && !expanded && overflows && "overflow-hidden",
            )}
            style={
              // A measured pixel cap cannot be a Tailwind class - the value is
              // a prop, and the alternative is one arbitrary class per caller.
              maxContentHeight && !expanded && overflows
                ? { maxHeight: maxContentHeight }
                : undefined
            }
          >
            {visibleChildren}
          </div>
          {/* A hard cut through the middle of a line reads as broken; a fade
              reads as "there is more". Non-interactive so it never swallows a
              click on the content beneath it. */}
          {maxContentHeight && !expanded && overflows && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-b from-transparent to-[var(--bg)]" />
          )}
        </div>

        {/* Height cap reached: reveal the rest. Separate from the row-count
            collapse below, because a card can be capped by height without
            having countable rows at all. */}
        {maxContentHeight && overflows && (
          <Button
            variant="ghost"
            onClick={() => setExpanded((v) => !v)}
            className="flex w-full items-center justify-center gap-1.5 rounded-none rounded-b-2xl border-t-[0.5px] border-t-[var(--rule-light)] py-3 text-[14px] font-medium text-[var(--ink-2)] transition-colors hover:text-[var(--ink)]"
          >
            {expanded ? (
              <>
                {t('detailCards.showLess', 'Show less')}{' '}
                <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                {t('detailCards.showMore', 'Show more')}{' '}
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </Button>
        )}

        {/* Expand/collapse toggle */}
        {shouldCollapse && (
          <Button
            variant="ghost"
            onClick={() => setExpanded((v) => !v)}
            className="flex w-full items-center justify-center gap-1.5 rounded-none rounded-b-2xl border-t-[0.5px] border-t-[var(--rule-light)] py-3 text-[14px] font-medium text-[var(--ink-2)] transition-colors hover:text-[var(--ink)]"
          >
            {isExpanded ? (
              <>
                {t('detailCards.showLess', 'Show less')}{' '}
                <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                {t('detailCards.showCountMore', 'Show {{count}} more', {
                  count: hiddenCount,
                })}{' '}
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

// ── FieldRow ────────────────────────────────────────────

interface FieldRowProps {
  label: string;
  /** Display value - shown when not editing */
  value?: unknown;
  /** When true, shows editComponent instead of value */
  editing?: boolean;
  /** The form input to show in edit mode */
  editComponent?: ReactNode;
  /** Make the value look like a link */
  link?: boolean;
  /** Callback when a link-styled value is clicked */
  onLinkClick?: () => void;
  /** Muted style for empty/placeholder values */
  muted?: boolean;
  /** Green positive styling (e.g. approved amount) */
  positive?: boolean;
}

export function FieldRow({
  label,
  value,
  editing = false,
  editComponent,
  link = false,
  onLinkClick,
  muted = false,
  positive = false,
}: FieldRowProps) {
  const displayValue = normaliseDetailCardDisplayValue(value) ?? "-";
  const isEmpty =
    displayValue === null ||
    displayValue === undefined ||
    displayValue === "" ||
    displayValue === "-";

  const row = (
    <div
      className={cn(
        "flex items-baseline justify-between gap-4 border-b-[0.5px] border-b-[var(--rule-light)] px-5 py-[10px] last:border-b-0",
        link && onLinkClick && "cursor-pointer transition-colors hover:bg-[var(--bg-subtle)]",
      )}
      onClick={link && onLinkClick ? onLinkClick : undefined}
      role={link && onLinkClick ? "button" : undefined}
    >
      <span data-slot="field-label" className={cn(
        // Allow long labels (e.g. the Solar PROtect question prompts) to
        // wrap and shrink instead of forcing the row wider than the card
        // and pushing the value past its right edge. Short labels are
        // unaffected - there is nothing to wrap.
        "min-w-0 break-words text-[14px] text-[var(--ink-2)]",
      )}>
        {label}
      </span>

      {editing && editComponent ? (
        <div className="flex-1 text-right">{editComponent}</div>
      ) : (
        <span
          className={cn(
            // Keep short values (e.g. "Yes") on a single line by not
            // shrinking below their content, but cap the width and allow
            // wrapping so a long value wraps instead of overflowing. The
            // label takes the remaining space and wraps.
            "max-w-[65%] shrink-0 break-words text-right text-[15px]",
            link && onLinkClick
              ? "text-[var(--green)]"
              : positive
                ? "text-[var(--green)]"
                : isEmpty || muted
                  ? "text-[var(--ink-3)]"
                  : "text-[var(--ink)]",
          )}
        >
          {isEmpty ? "-" : displayValue}
        </span>
      )}
    </div>
  );

  return row;
}

